import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useI18n } from "@/i18n/I18nContext";
import {
  isPaddleConfigured,
  openPaddleCheckout,
} from "@/lib/paddle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const METHOD_ICONS = { card: "💳", mpesa: "📱", tigo: "📶" };

export default function CheckoutModal({ open, onClose }) {
  const { t, locale } = useI18n();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: "", email: "", phone: "", method: "card" });
  const [errors, setErrors] = useState({});
  const [order, setOrder] = useState(null);
  const [paddleReady, setPaddleReady] = useState(false);
  const [paddleTxnId, setPaddleTxnId] = useState(null);
  const [pollError, setPollError] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    // Fetch server config and client-token/price-id at runtime. Backend reads
    // env at request time, so flipping PADDLE_ENVIRONMENT=production on the
    // server instantly flips the overlay to live mode — no frontend rebuild
    // needed.
    (async () => {
      try {
        const cfg = await axios.get(`${API}/paddle/config`);
        const configuredOnServer = Boolean(cfg.data?.configured);
        const configuredOnClient = await isPaddleConfigured();
        setPaddleReady(configuredOnServer && configuredOnClient);
      } catch (e) {
        setPaddleReady(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => {
        setStep(1);
        setForm({ name: "", email: "", phone: "", method: "card" });
        setErrors({});
        setOrder(null);
        setPaddleTxnId(null);
        setPollError(null);
        if (pollRef.current) clearTimeout(pollRef.current);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const validate = () => {
    const errs = {};
    if (!form.name || form.name.trim().length < 2) errs.name = t.checkout.err_name;
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = t.checkout.err_email;
    if (!form.phone || form.phone.replace(/[^\d]/g, "").length < 7) errs.phone = t.checkout.err_phone;
    return errs;
  };

  const handleNext = () => {
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length === 0) setStep(2);
  };

  // ---- Real Paddle flow ----
  const startPaddle = async () => {
    try {
      await openPaddleCheckout({
        name: form.name,
        email: form.email,
        phone: form.phone,
        locale,
        onEvent: (event) => {
          if (!event) return;
          if (event.name === "checkout.completed") {
            const txn = event?.data?.transaction_id;
            if (txn) {
              setPaddleTxnId(txn);
              setStep(3);
              pollFulfilment(txn);
            }
          }
          if (event.name === "checkout.error") {
            setPollError("Paddle checkout error — please try again.");
          }
        },
      });
    } catch (e) {
      setPollError(String(e?.message || e));
    }
  };

  const pollFulfilment = async (txnId) => {
    const started = Date.now();
    const tick = async () => {
      try {
        const res = await axios.get(`${API}/purchases/${encodeURIComponent(txnId)}`);
        const s = res.data;
        if (s.state === "sent") {
          setOrder({
            order_id: s.order_id,
            email: s.email || form.email,
            phone: form.phone,
            amount: 3.0,
            currency: "USD",
            download_url: s.download_url,
            email_sent: !!s.email_sent,
          });
          setStep(4);
          return;
        }
        if (s.state === "failed") {
          setPollError("Fulfilment failed — please contact support with your transaction id.");
          setOrder({
            order_id: txnId,
            email: form.email,
            phone: form.phone,
            amount: 3.0,
            currency: "USD",
            error: true,
            email_sent: false,
          });
          setStep(4);
          return;
        }
        if (Date.now() - started > 90_000) {
          setPollError("Still processing — please refresh in a moment. Your PDF will arrive by email.");
          return;
        }
        pollRef.current = setTimeout(tick, 2000);
      } catch (e) {
        pollRef.current = setTimeout(tick, 3000);
      }
    };
    tick();
  };

  // ---- Mock flow (fallback) ----
  const handleMockPay = async () => {
    setStep(3);
    try {
      const res = await axios.post(`${API}/checkout/mock`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        method: form.method,
        locale,
      });
      setOrder(res.data);
      setStep(4);
    } catch (e) {
      setOrder({
        order_id: "ERR-" + Math.random().toString(36).slice(2, 7).toUpperCase(),
        email: form.email,
        phone: form.phone,
        amount: 3,
        currency: "USD",
        email_sent: false,
        download_url: null,
        error: true,
      });
      setStep(4);
    }
  };

  const handlePay = () => (paddleReady ? startPaddle() : handleMockPay());

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-6"
          data-testid="paddle-checkout-modal"
        >
          <div
            className="absolute inset-0 bg-[rgba(15,31,23,0.75)] backdrop-blur-md"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: 60, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
            className="relative bg-[var(--cream)] rounded-[26px] max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
          >
            <div className="flex items-center justify-between p-5 md:p-6 border-b border-[rgba(15,31,23,0.08)]">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[var(--ink)] text-[var(--cream)] flex items-center justify-center">
                  <svg viewBox="0 0 64 64" width="20" height="20">
                    <path d="M32 12c6 8 12 15 12 22a12 12 0 1 1-24 0c0-7 6-14 12-22z" fill="#FDFBF7"/>
                    <circle cx="32" cy="38" r="4.5" fill="#E65C00"/>
                  </svg>
                </div>
                <div>
                  <div className="font-display text-lg leading-none">{t.checkout.title}</div>
                  <div className="text-[11px] text-[var(--muted)] mt-1 font-mono-caption tracking-[0.2em] uppercase">
                    {t.checkout.subtitle_a}
                    {!paddleReady && (
                      <>
                        {" "}
                        <span className="text-[var(--marigold)]">{t.checkout.subtitle_mock}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-full border border-[rgba(15,31,23,0.12)] hover:bg-[var(--cream-2)] flex items-center justify-center"
                data-testid="checkout-close"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              {step < 4 && (
                <div className="p-5 md:p-6 border-b border-[rgba(15,31,23,0.06)] bg-[var(--cream-2)]/60 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-16 rounded-md flex items-center justify-center bg-[var(--ink)] text-[var(--cream)] font-display text-xs leading-tight text-center">
                      From<br/>Milk<br/>2Income
                    </div>
                    <div>
                      <div className="font-display text-base leading-tight">{t.checkout.order_line_1}</div>
                      <div className="text-[12px] text-[var(--muted)]">{t.checkout.order_line_2}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl">$3.00</div>
                    <div className="text-[11px] text-[var(--muted)] line-through">$29.00</div>
                  </div>
                </div>
              )}

              <div className="p-6 md:p-8">
                <AnimatePresence mode="wait">
                  {step === 1 && (
                    <motion.div key="s1" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.3 }}>
                      <div className="font-mono-caption text-[10px] tracking-[0.22em] text-[var(--marigold)] uppercase mb-2">
                        {t.checkout.step1_pre}
                      </div>
                      <h3 className="font-display text-2xl md:text-3xl leading-tight tracking-tight mb-2">
                        {t.checkout.step1_h_a}{" "}
                        <span className="font-editorial-italic text-[var(--marigold)]">
                          {t.checkout.step1_h_italic}
                        </span>{" "}
                        {t.checkout.step1_h_b}
                      </h3>
                      <p className="text-[13px] text-[var(--muted)] mb-6">{t.checkout.step1_p}</p>

                      <Field label={t.checkout.f_name} placeholder={t.checkout.f_name_ph}
                        value={form.name} onChange={(v) => setForm({ ...form, name: v })}
                        error={errors.name} testid="checkout-name-input" />
                      <Field label={t.checkout.f_email} type="email" placeholder={t.checkout.f_email_ph}
                        value={form.email} onChange={(v) => setForm({ ...form, email: v })}
                        error={errors.email} testid="checkout-email-input" />
                      <Field label={t.checkout.f_phone} type="tel" placeholder={t.checkout.f_phone_ph}
                        value={form.phone} onChange={(v) => setForm({ ...form, phone: v })}
                        error={errors.phone} testid="checkout-phone-input" />

                      <button onClick={handleNext} className="btn-pill btn-primary w-full mt-4" data-testid="checkout-next-button">
                        {t.checkout.next}
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </motion.div>
                  )}

                  {step === 2 && (
                    <motion.div key="s2" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.3 }}>
                      <div className="font-mono-caption text-[10px] tracking-[0.22em] text-[var(--marigold)] uppercase mb-2">
                        {t.checkout.step2_pre}
                      </div>
                      <h3 className="font-display text-2xl md:text-3xl leading-tight tracking-tight mb-6">
                        {t.checkout.step2_h_a}{" "}
                        <span className="font-editorial-italic text-[var(--marigold)]">
                          {t.checkout.step2_h_price}
                        </span>{" "}
                        {t.checkout.step2_h_b}
                      </h3>

                      {paddleReady ? (
                        <div className="mb-6 rounded-2xl border border-[rgba(15,31,23,0.1)] p-5 bg-[var(--cream-2)]/50">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-full bg-[var(--jade)]/15 text-[var(--jade)] flex items-center justify-center">
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z" stroke="currentColor" strokeWidth="1.8" fill="none"/>
                                <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                              </svg>
                            </div>
                            <div>
                              <div className="font-medium">Secure checkout via Paddle</div>
                              <div className="text-[12px] text-[var(--muted)]">
                                Card, Google Pay, Apple Pay, PayPal, and local methods.
                              </div>
                            </div>
                          </div>
                          <p className="text-[12px] text-[var(--muted)]">
                            Your name, email &amp; phone are sent to Paddle only to identify your
                            purchase and stamp your PDF. Card data never touches our servers.
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="grid gap-3 mb-6">
                            {t.checkout.methods.map((m) => (
                              <button
                                key={m.id}
                                onClick={() => setForm({ ...form, method: m.id })}
                                className={`text-left p-4 rounded-xl border-2 flex items-center gap-4 transition-all ${
                                  form.method === m.id
                                    ? "border-[var(--marigold)] bg-[var(--cream-2)]"
                                    : "border-[rgba(15,31,23,0.1)] hover:border-[rgba(15,31,23,0.25)]"
                                }`}
                                data-testid={`checkout-method-${m.id}`}
                              >
                                <span className="text-2xl">{METHOD_ICONS[m.id]}</span>
                                <div className="flex-1">
                                  <div className="font-medium">{m.label}</div>
                                  <div className="text-[12px] text-[var(--muted)]">{m.sub}</div>
                                </div>
                                <span className={`w-5 h-5 rounded-full border-2 ${form.method === m.id ? "border-[var(--marigold)] bg-[var(--marigold)]" : "border-[rgba(15,31,23,0.25)]"}`} />
                              </button>
                            ))}
                          </div>
                          <p className="text-[11px] text-[var(--muted)] mb-6">{t.checkout.card_note}</p>
                        </>
                      )}

                      {pollError && (
                        <div className="mb-4 rounded-xl border border-red-300 bg-red-50 text-red-800 p-3 text-[13px]">
                          {pollError}
                        </div>
                      )}

                      <div className="flex flex-col-reverse sm:flex-row gap-3">
                        <button onClick={() => setStep(1)} className="btn-pill btn-ghost-dark sm:flex-shrink-0" data-testid="checkout-back-button">
                          {t.checkout.back}
                        </button>
                        <button onClick={handlePay} className="btn-pill btn-primary flex-1" data-testid="checkout-submit-button">
                          {paddleReady ? "Continue to Paddle · $3" : t.checkout.submit}
                        </button>
                      </div>
                    </motion.div>
                  )}

                  {step === 3 && (
                    <motion.div key="s3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-14 flex flex-col items-center text-center" data-testid="checkout-processing">
                      <div className="relative w-20 h-20 mb-6">
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
                          className="absolute inset-0 rounded-full border-[3px] border-[var(--cream-2)] border-t-[var(--marigold)]"
                        />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <svg viewBox="0 0 64 64" width="28" height="28">
                            <path d="M32 12c6 8 12 15 12 22a12 12 0 1 1-24 0c0-7 6-14 12-22z" fill="var(--ink)"/>
                          </svg>
                        </div>
                      </div>
                      <h3 className="font-display text-2xl leading-tight tracking-tight">{t.checkout.step3_h}</h3>
                      <p className="mt-2 text-[13px] text-[var(--muted)] max-w-[42ch]">
                        {t.checkout.step3_p_a}{" "}
                        <span className="font-mono-caption text-[var(--ink)]">{form.email}</span>{" "}
                        {t.checkout.step3_p_and}{" "}
                        <span className="font-mono-caption text-[var(--ink)]">{form.phone}</span>{" "}
                        {t.checkout.step3_p_b}
                      </p>
                      {paddleTxnId && (
                        <p className="mt-4 text-[11px] font-mono-caption text-[var(--muted)]">
                          Paddle txn: <span className="text-[var(--ink-2)]">{paddleTxnId}</span>
                        </p>
                      )}
                    </motion.div>
                  )}

                  {step === 4 && order && (
                    <motion.div
                      key="s4"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      data-testid="thank-you-container"
                    >
                      <div className="flex items-center gap-3 mb-6">
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
                          className="w-12 h-12 rounded-full bg-[var(--jade)] text-[var(--cream)] flex items-center justify-center"
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M5 12l4 4L19 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
                          </svg>
                        </motion.div>
                        <div>
                          <div className="font-mono-caption text-[10px] tracking-[0.22em] uppercase text-[var(--jade)]">
                            {t.checkout.step4_pre}
                          </div>
                          <h3 className="font-display text-2xl md:text-3xl leading-tight tracking-tight">
                            {t.checkout.step4_h_a} {form.name.split(" ")[0]} —{" "}
                            <span className="font-editorial-italic text-[var(--marigold)]">
                              {t.checkout.step4_h_italic}
                            </span>
                          </h3>
                        </div>
                      </div>

                      <div className="rounded-2xl bg-[var(--cream-2)] p-5 mb-4">
                        <div className="grid grid-cols-2 gap-3 text-[13.5px]">
                          <Row k={t.checkout.row_order} v={order.order_id} />
                          <Row k={t.checkout.row_amount} v={`$${(order.amount || 3).toFixed(2)} ${order.currency || "USD"}`} />
                          <Row k={t.checkout.row_delivered} v={order.email} />
                          <Row k={t.checkout.row_watermark} v={order.phone} />
                        </div>
                      </div>

                      <div className={`flex items-start gap-2 rounded-xl p-3 mb-4 text-[13px] ${
                        order.email_sent
                          ? "bg-[#E9F6EE] text-[#0A5A38] border border-[#B7E1C7]"
                          : "bg-[#FFF7E1] text-[#8A5A00] border border-[#F1B24A]"
                      }`}>
                        <span className="shrink-0 mt-0.5">{order.email_sent ? "✉️" : "⚠️"}</span>
                        <span>
                          {order.email_sent
                            ? `${t.checkout.email_sent_ok} → ${order.email}`
                            : t.checkout.email_sent_fallback}
                        </span>
                      </div>

                      {order.download_url && (
                        <div className="rounded-2xl border border-[rgba(15,31,23,0.15)] p-5 mb-5 bg-[var(--ink)] text-[var(--cream)]">
                          <div className="font-mono-caption text-[10px] tracking-[0.22em] uppercase text-[var(--marigold-2)] mb-3">
                            {t.checkout.row_watermark.split(" ")[0]} PDF
                          </div>
                          <a
                            href={order.download_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-pill btn-primary w-full text-[15px]"
                            data-testid="thank-you-download-button"
                          >
                            <span>⬇ {t.checkout.dl_button}</span>
                          </a>
                          <p className="mt-3 text-[11.5px] text-[var(--cream)]/60 leading-relaxed">
                            {t.checkout.dl_note}
                          </p>
                        </div>
                      )}

                      <div className="rounded-2xl border border-dashed border-[rgba(15,31,23,0.2)] p-5 mb-5">
                        <div className="font-mono-caption text-[10px] tracking-[0.22em] uppercase text-[var(--muted)] mb-2">
                          {t.checkout.next_title}
                        </div>
                        <ol className="space-y-2 text-[14px] text-[var(--ink-2)]/85">
                          <li className="flex gap-2">
                            <span className="font-mono-caption text-[var(--marigold)]">01.</span>
                            <span>{t.checkout.step_1_txt} <b>{order.email}</b>.</span>
                          </li>
                          <li className="flex gap-2">
                            <span className="font-mono-caption text-[var(--marigold)]">02.</span>
                            <span>{t.checkout.step_2_txt}</span>
                          </li>
                          <li className="flex gap-2">
                            <span className="font-mono-caption text-[var(--marigold)]">03.</span>
                            <span>{t.checkout.step_3_txt}</span>
                          </li>
                        </ol>
                      </div>

                      <button onClick={onClose} className="btn-pill btn-ghost-dark w-full" data-testid="thank-you-close-button">
                        {t.checkout.thanks_close}
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const Field = ({ label, value, onChange, placeholder, type = "text", error, disabled, testid }) => (
  <div className="mb-4">
    <label className="block text-[12px] font-medium text-[var(--ink-2)] mb-1.5">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      data-testid={testid}
      className={`w-full rounded-xl border px-4 py-3 text-[15px] bg-[var(--cream)] focus:outline-none transition-colors ${
        error ? "border-red-500" : "border-[rgba(15,31,23,0.15)] focus:border-[var(--marigold)]"
      } disabled:opacity-60`}
    />
    {error && <p className="mt-1.5 text-[12px] text-red-600">{error}</p>}
  </div>
);

const Row = ({ k, v }) => (
  <div>
    <div className="font-mono-caption text-[10px] tracking-[0.18em] uppercase text-[var(--muted)]">{k}</div>
    <div className="text-[var(--ink)] font-medium break-all">{v}</div>
  </div>
);
